import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getRelatorioDiario } from "./api";
import "./RelatorioDiarioPage.css";

const DONUT_SIZE = 188;
const DONUT_STROKE = 22;
const DONUT_CENTER = DONUT_SIZE / 2;
const DONUT_RADIUS = (DONUT_SIZE - DONUT_STROKE) / 2;
const DONUT_CIRC = 2 * Math.PI * DONUT_RADIUS;
const TREND_PLOT = { left: 6, right: 94, top: 10, bottom: 84 };

function trendPctToY(pct) {
  const safe = Math.max(0, Math.min(100, Number(pct || 0)));
  return TREND_PLOT.bottom - (safe / 100) * (TREND_PLOT.bottom - TREND_PLOT.top);
}

function todayYMD() {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear()}-${mm}-${dd}`;
  }
}

function addDaysYMD(day, delta) {
  try {
    const base = new Date(`${day}T12:00:00Z`);
    base.setUTCDate(base.getUTCDate() + delta);
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(base);
  } catch {
    return day;
  }
}

function fmtDayLabel(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(`${value}T00:00:00`));
  } catch {
    return value;
  }
}

function fmtDayShort(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
    }).format(new Date(`${value}T00:00:00`));
  } catch {
    return value;
  }
}

function startOfWeekYMD(day) {
  if (!day) return todayYMD();
  try {
    const base = new Date(`${day}T12:00:00Z`);
    const dayOfWeek = base.getUTCDay(); // 0 = domingo
    const diffToMonday = (dayOfWeek + 6) % 7; // segunda = 0
    base.setUTCDate(base.getUTCDate() - diffToMonday);
    const mm = String(base.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(base.getUTCDate()).padStart(2, "0");
    return `${base.getUTCFullYear()}-${mm}-${dd}`;
  } catch {
    return day;
  }
}

function fmtSeconds(value) {
  const total = Number(value || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (!h && !m) return "0h";
  if (!h) return `${m}m`;
  if (!m) return `${h}h`;
  return `${h}h ${m}m`;
}

function buildSmoothTrendPath(points, tension = 0.16) {
  if (!points?.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;

    const cp1x = p1.x + (p2.x - p0.x) * tension;
    const cp1y = p1.y + (p2.y - p0.y) * tension;
    const cp2x = p2.x - (p3.x - p1.x) * tension;
    const cp2y = p2.y - (p3.y - p1.y) * tension;

    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function clampPct(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function getPerformanceScale(value) {
  const pct = clampPct(value);
  if (pct < 50) return { tone: "bad", color: "#ef4444", label: "Ruim" };
  if (pct < 65) return { tone: "midlow", color: "#f97316", label: "Medio baixo" };
  if (pct < 80) return { tone: "mid", color: "#eab308", label: "Medio" };
  if (pct < 95) return { tone: "midhigh", color: "#84cc16", label: "Medio alto" };
  return { tone: "good", color: "#0f766e", label: "Bom" };
}

function getMetaScale(metaPct) {
  const pct = clampPct(metaPct);
  if (pct < 60) return { tone: "bad", color: "#ef4444", label: "Ruim" };
  if (pct < 70) return { tone: "midlow", color: "#f97316", label: "Medio baixo" };
  if (pct < 80) return { tone: "mid", color: "#eab308", label: "Medio" };
  if (pct < 90) return { tone: "midhigh", color: "#84cc16", label: "Medio alto" };
  return { tone: "good", color: "#0f766e", label: "Bom" };
}

function getInstabilityScale(instabilityPct) {
  const pct = clampPct(instabilityPct);
  if (pct <= 10) return { tone: "good", color: "#0f766e", label: "Bom" };
  if (pct <= 20) return { tone: "midhigh", color: "#84cc16", label: "Medio alto" };
  if (pct <= 30) return { tone: "mid", color: "#eab308", label: "Medio" };
  if (pct <= 40) return { tone: "midlow", color: "#f97316", label: "Medio baixo" };
  return { tone: "bad", color: "#ef4444", label: "Ruim" };
}

function ParetoChart({ title, subtitle, rows }) {
  const paretoPlot = { top: 10, bottom: 84 };
  const mapCumToY = useCallback(
    (cumPct) =>
      paretoPlot.bottom -
      (Math.max(0, Math.min(100, Number(cumPct || 0))) / 100) * (paretoPlot.bottom - paretoPlot.top),
    []
  );

  const maxTotal = useMemo(
    () => rows.reduce((m, r) => Math.max(m, Number(r.total || 0)), 0) || 1,
    [rows]
  );

  const points = useMemo(() => {
    if (!rows.length) return [];
    const step = 100 / Math.max(rows.length, 1);
    return rows.map((r, idx) => {
      const cum = Math.max(0, Math.min(100, Number(r.cum || 0)));
      const x = (idx + 0.5) * step;
      const y = mapCumToY(cum);
      return { x, y, cum };
    });
  }, [rows, mapCumToY]);

  const polyline = useMemo(
    () => points.map((p) => `${p.x},${p.y}`).join(" "),
    [points]
  );
  const smoothPath = useMemo(() => buildSmoothTrendPath(points, 0.2), [points]);
  const paretoThresholdY = mapCumToY(80);

  return (
    <div className="rd-card rd-pareto-card">
      <div className="rd-card-head">
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </div>
      <div className="rd-pareto-chart">
        <div className="rd-pareto-plot">
          <div className="rd-pareto-bars">
            {rows.map((r) => {
              const height = Math.round((Number(r.total || 0) / maxTotal) * 100);
              return (
                <div key={r.causador} className="rd-pareto-bar-col">
                  <div className="rd-pareto-bar-shell">
                    <div className="rd-pareto-bar-fill" style={{ height: `${height}%` }} />
                  </div>
                  <div className="rd-pareto-bar-value">{r.total}</div>
                  <span>{r.causador}</span>
                </div>
              );
            })}
          </div>
          <svg className="rd-pareto-line" viewBox="0 0 100 100" preserveAspectRatio="none">
            <line
              x1="0"
              y1={paretoThresholdY}
              x2="100"
              y2={paretoThresholdY}
              className="rd-pareto-threshold"
              fill="none"
            />
            {points.map((p, idx) => (
              <line
                key={`g-${idx}`}
                x1={p.x}
                y1={paretoPlot.bottom}
                x2={p.x}
                y2={p.y}
                className="rd-pareto-guide"
                fill="none"
              />
            ))}
            {smoothPath ? (
              <path
                d={smoothPath}
                className="rd-pareto-curve"
                fill="none"
                stroke="#f97316"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              polyline && (
                <polyline
                  points={polyline}
                  className="rd-pareto-poly"
                  fill="none"
                  stroke="#f97316"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )
            )}
            {points.map((p, idx) => (
              <circle key={idx} cx={p.x} cy={p.y} r="2" className="rd-pareto-dot-point" />
            ))}
          </svg>
          {points.map((p, idx) => (
            <div
              key={`v-${idx}`}
              className="rd-pareto-point-label"
              style={{ left: `${p.x}%`, top: `${Math.max(10, p.y - 4)}%` }}
            >
              {p.cum}%
            </div>
          ))}
        </div>
        <div className="rd-pareto-legend">
          <span className="rd-pareto-dot" /> Linha acumulada (%)
          <span className="rd-pareto-cut">80%</span>
        </div>
      </div>
      {!rows.length && <div className="rd-empty">Sem causadores no periodo.</div>}
    </div>
  );
}

function RelatorioDiarioSection({ label, day, data }) {
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const prefixRows = Array.isArray(data?.prefix) ? data.prefix : [];
  const atividadesRows = Array.isArray(data?.atividades) ? data.atividades : [];
  const paretoFinalizados = Array.isArray(data?.paretoFinalizados) ? data.paretoFinalizados : [];
  const paretoReprogramados = Array.isArray(data?.paretoReprogramados) ? data.paretoReprogramados : [];
  const previousTotals = data?.previousTotals || null;
  const trendRows = Array.isArray(data?.trendRows) ? data.trendRows : [];
  const trendStart = data?.trendStart || "";
  const trendEnd = data?.trendEnd || "";

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.programados += Number(r.programados || 0);
        acc.finalizados += Number(r.finalizados || 0);
        acc.reprogramados += Number(r.reprogramados || 0);
        acc.abertos += Number(r.abertos || 0);
        acc.prefixoE += Number(r.prefixo_e || 0);
        return acc;
      },
      { programados: 0, finalizados: 0, reprogramados: 0, abertos: 0, prefixoE: 0 }
    );
  }, [rows]);

  const completion = useMemo(() => {
    if (!totals.programados) return 0;
    return Math.round((totals.finalizados / totals.programados) * 100);
  }, [totals]);

  const prefixTotals = useMemo(() => {
    return prefixRows.reduce(
      (acc, r) => {
        acc.d += Number(r.prefixo_d || 0);
        acc.e += Number(r.prefixo_e || 0);
        acc.s += Number(r.prefixo_semanal || 0);
        return acc;
      },
      { d: 0, e: 0, s: 0 }
    );
  }, [prefixRows]);

  const prefixSegments = useMemo(() => {
    const base = [
      { key: "d", label: "Dia anterior", value: prefixTotals.d, color: "#0f766e", dotClass: "dot-a" },
      { key: "e", label: "No dia", value: prefixTotals.e, color: "#f97316", dotClass: "dot-b" },
      { key: "s", label: "Semanal", value: prefixTotals.s, color: "#94a3b8", dotClass: "dot-c" },
    ];
    const total = base.reduce((acc, item) => acc + item.value, 0);
    if (!total) {
      return base.map((item) => ({
        ...item,
        pct: 0,
        startDeg: 0,
        endDeg: 0,
        midDeg: 0,
      }));
    }

    let cursor = 0;
    return base.map((item) => {
      const slice = (item.value / total) * 360;
      const startDeg = cursor;
      const endDeg = cursor + slice;
      cursor = endDeg;
      return {
        ...item,
        pct: Math.round((item.value / total) * 100),
        startDeg,
        endDeg,
        midDeg: startDeg + slice / 2,
      };
    });
  }, [prefixTotals]);

  const prefixTotal = prefixSegments.reduce((acc, item) => acc + item.value, 0);
  const prefixActiveSegments = prefixSegments.filter((item) => item.value > 0);
  const prefixArcSegments = useMemo(() => {
    if (!prefixActiveSegments.length || !prefixTotal) return [];
    const gapPx = prefixActiveSegments.length > 1 ? 2.4 : 0;
    let cursor = 0;
    return prefixActiveSegments.map((item) => {
      const rawArc = (item.value / prefixTotal) * DONUT_CIRC;
      const arc = Math.max(0, rawArc - gapPx);
      const seg = {
        ...item,
        arc,
        offset: -cursor,
      };
      cursor += rawArc;
      return seg;
    });
  }, [prefixActiveSegments, prefixTotal]);
  const prefixLabelPositions = useMemo(
    () =>
      prefixActiveSegments.map((item) => {
        const distance = item.pct <= 10 ? 81 : item.pct <= 20 ? 74 : 67;
        const rad = (item.midDeg * Math.PI) / 180;
        return {
          ...item,
          labelX: DONUT_CENTER + distance * Math.sin(rad),
          labelY: DONUT_CENTER - distance * Math.cos(rad),
        };
      }),
    [prefixActiveSegments]
  );

  const atividadesOrdenadas = useMemo(() => {
    return [...atividadesRows].sort((a, b) =>
      String(a.atividade || "").localeCompare(String(b.atividade || ""), "pt-BR")
    );
  }, [atividadesRows]);

  const tempoTotals = useMemo(() => {
    return atividadesRows.reduce(
      (acc, r) => {
        acc.planejado += Number(r.tempo_planejado_s || 0);
        acc.executado += Number(r.tempo_executado_s || 0);
        return acc;
      },
      { planejado: 0, executado: 0 }
    );
  }, [atividadesRows]);

  const quantidadeTotals = useMemo(() => {
    return atividadesRows.reduce(
      (acc, r) => {
        acc.planejado += Number(r.quantidade_planejada || 0);
        acc.executado += Number(r.quantidade_executada || 0);
        return acc;
      },
      { planejado: 0, executado: 0 }
    );
  }, [atividadesRows]);

  const paretoFinalizadoRows = useMemo(() => {
    const total = paretoFinalizados.reduce((s, r) => s + Number(r.total || 0), 0) || 1;
    let acc = 0;
    return paretoFinalizados.map((r) => {
      acc += Number(r.total || 0);
      const pct = Math.round((Number(r.total || 0) / total) * 100);
      const cum = Math.min(100, Math.max(0, Math.round((acc / total) * 100)));
      return { ...r, pct, cum };
    });
  }, [paretoFinalizados]);

  const paretoReprogramadoRows = useMemo(() => {
    const total = paretoReprogramados.reduce((s, r) => s + Number(r.total || 0), 0) || 1;
    let acc = 0;
    return paretoReprogramados.map((r) => {
      acc += Number(r.total || 0);
      const pct = Math.round((Number(r.total || 0) / total) * 100);
      const cum = Math.min(100, Math.max(0, Math.round((acc / total) * 100)));
      return { ...r, pct, cum };
    });
  }, [paretoReprogramados]);

  const tempoPct = tempoTotals.planejado
    ? Math.round((tempoTotals.executado / tempoTotals.planejado) * 100)
    : 0;
  const quantidadePct = quantidadeTotals.planejado
    ? Math.round((quantidadeTotals.executado / quantidadeTotals.planejado) * 100)
    : 0;
  const completionScale = useMemo(() => getPerformanceScale(completion), [completion]);
  const tempoScale = useMemo(() => getPerformanceScale(tempoPct), [tempoPct]);
  const quantidadeScale = useMemo(() => getPerformanceScale(quantidadePct), [quantidadePct]);
  const completionArc = useMemo(() => (clampPct(completion) / 100) * DONUT_CIRC, [completion]);
  const tempoArc = useMemo(() => (clampPct(tempoPct) / 100) * DONUT_CIRC, [tempoPct]);
  const quantidadeArc = useMemo(() => (clampPct(quantidadePct) / 100) * DONUT_CIRC, [quantidadePct]);

  const metaPctRaw = totals.programados
    ? (totals.finalizados / totals.programados) * 100
    : 0;
  const metaPct = Math.max(0, Math.min(100, metaPctRaw));
  const metaPctDisplay = Number(metaPct.toFixed(1));
  const metaScale = getMetaScale(metaPct);

  const estabilidadePctRaw = totals.programados
    ? (totals.reprogramados / totals.programados) * 100
    : 0;
  const estabilidadePct = Math.max(0, Math.min(100, estabilidadePctRaw));
  let estabilidadePctDisplay = Number(estabilidadePct.toFixed(1));
  const somaIndicadores = metaPctDisplay + estabilidadePctDisplay;
  if (somaIndicadores > 100) {
    estabilidadePctDisplay = Math.max(
      0,
      Number((estabilidadePctDisplay - (somaIndicadores - 100)).toFixed(1))
    );
  }
  const estabilidadeScale = getInstabilityScale(estabilidadePct);

  const compareItems = useMemo(() => {
    const prev = previousTotals || {};
    const makeDelta = (curr, prevValue) => {
      const pv = Number(prevValue || 0);
      if (!pv) return "N/A";
      const diff = curr - pv;
      const pct = Math.round((diff / pv) * 100);
      return `${diff >= 0 ? "+" : ""}${pct}%`;
    };

    return [
      {
        label: "Programados",
        value: totals.programados,
        prev: Number(prev.programados || 0),
        delta: makeDelta(totals.programados, prev.programados),
      },
      {
        label: "Finalizados",
        value: totals.finalizados,
        prev: Number(prev.finalizados || 0),
        delta: makeDelta(totals.finalizados, prev.finalizados),
      },
      {
        label: "Reprogramados",
        value: totals.reprogramados,
        prev: Number(prev.reprogramados || 0),
        delta: makeDelta(totals.reprogramados, prev.reprogramados),
      },
    ];
  }, [previousTotals, totals]);

  const trendSeries = useMemo(() => {
    const referenceDay = day || trendEnd || trendStart || todayYMD();
    const weekStart = startOfWeekYMD(referenceDay);
    const fullWeekDays = Array.from({ length: 7 }, (_, idx) => addDaysYMD(weekStart, idx));
    const map = new Map(
      (trendRows || []).map((r) => [
        r.data,
        {
          programados: Number(r.programados || 0),
          finalizados: Number(r.finalizados || 0),
        },
      ])
    );

    return fullWeekDays.map((d) => {
      const entry = map.get(d);
      if (!entry || entry.programados <= 0) {
        return { data: d, pct: null, hasData: false };
      }
      const pct = Math.round((entry.finalizados / entry.programados) * 100);
      return { data: d, pct: Math.max(0, Math.min(100, pct)), hasData: true };
    });
  }, [trendRows, trendStart, trendEnd, day]);

  const trendPoints = useMemo(() => {
    if (!trendSeries.length) return [];
    const last = trendSeries.length - 1;
    return trendSeries.map((d, idx) => ({
      x: last <= 0 ? 50 : TREND_PLOT.left + (idx / last) * (TREND_PLOT.right - TREND_PLOT.left),
      y: d.hasData ? trendPctToY(d.pct) : TREND_PLOT.bottom,
      value: d.pct,
      label: d.data,
      hasData: d.hasData,
      color: d.hasData ? getMetaScale(d.pct).color : "#94a3b8",
    }));
  }, [trendSeries]);

  const trendPlotPoints = useMemo(
    () => trendPoints.filter((p) => p.hasData && Number.isFinite(p.value)),
    [trendPoints]
  );
  const trendSmoothPath = useMemo(
    () => buildSmoothTrendPath(trendPlotPoints),
    [trendPlotPoints]
  );
  const trendGradientId = useMemo(
    () => `rd-trend-grad-${String(label || "geral").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`,
    [label]
  );

  const trendTargetPct = 90;
  const trendTargetY = trendPctToY(trendTargetPct);
  const trendResume = useMemo(() => {
    const valid = trendSeries.filter((d) => d.hasData && Number.isFinite(d.pct));
    if (!valid.length) return { start: 0, end: 0 };
    return {
      start: valid[0]?.pct ?? 0,
      end: valid[valid.length - 1]?.pct ?? 0,
    };
  }, [trendSeries]);
  const trendThresholdColor = getMetaScale(trendResume.end).color;

  const desviosTop = useMemo(() => {
    const base = atividadesRows.map((r) => {
      const tempoPlan = Number(r.tempo_planejado_s || 0);
      const tempoExec = Number(r.tempo_executado_s || 0);
      const qtdPlan = Number(r.quantidade_planejada || 0);
      const qtdExec = Number(r.quantidade_executada || 0);

      const tempoDiff = tempoExec - tempoPlan;
      const tempoPctAbs = tempoPlan
        ? Math.round((Math.abs(tempoDiff) / tempoPlan) * 100)
        : 0;
      const qtdDiff = qtdExec - qtdPlan;
      const qtdPctAbs = qtdPlan ? Math.round((Math.abs(qtdDiff) / qtdPlan) * 100) : 0;

      const negScore = Math.max(
        tempoDiff < 0 ? tempoPctAbs : 0,
        qtdDiff < 0 ? qtdPctAbs : 0
      );
      const posScore = Math.max(
        tempoDiff > 0 ? tempoPctAbs : 0,
        qtdDiff > 0 ? qtdPctAbs : 0
      );

      return {
        atividade: r.atividade,
        tempoDiff,
        tempoPctAbs,
        qtdDiff,
        qtdPctAbs,
        negScore,
        posScore,
      };
    });

    const pior = base
      .filter((d) => d.negScore > d.posScore && d.negScore > 0)
      .sort((a, b) => b.negScore - a.negScore)
      .slice(0, 5);

    const melhor = base
      .filter((d) => d.negScore === 0)
      .sort((a, b) => b.posScore - a.posScore)
      .slice(0, 5);

    return { pior, melhor };
  }, [atividadesRows]);

  const kpis = [
    { label: "Programados", value: totals.programados },
    { label: "Finalizados", value: totals.finalizados },
    { label: "Reprogramados", value: totals.reprogramados },
    { label: "Criados Fora do Esperado", value: totals.prefixoE },
    { label: "Em aberto", value: totals.abertos },
  ];

  const labelRange = day ? `Dia ${fmtDayLabel(day)}` : "";
  const isGeral = !label || label === "Geral";

  return (
    <div className="rd-section">
      <header
        className="rd-hero"
        data-email-capture="cards"
        data-email-name={`Resumo operacional - ${isGeral ? "Geral" : label}`}
      >
        <div className="rd-hero-text">
          {isGeral && <span className="rd-pill">Relatorio diario</span>}
          <h1>{isGeral ? `Resumo operacional - ${labelRange}` : label}</h1>
          {isGeral && (
            <>
              <div className="rd-hero-sub">Geral</div>
              <p>
                Comparativo entre planejado e executado, com foco no que ja entrou em producao.
              </p>
            </>
          )}
        </div>

        <div className="rd-hero-card" data-email-capture="cards" data-email-name="Resumo geral">
          <div className="rd-hero-card-head">
            <strong>Resumo geral</strong>
            <span>Visao consolidada do periodo</span>
          </div>
          <div className="rd-hero-grid">
            <div className="rd-hero-metric">
              <span>Taxa de conclusao</span>
              <strong>{completion}%</strong>
            </div>
            <div className="rd-hero-metric">
              <span>Programados</span>
              <strong>{totals.programados}</strong>
            </div>
            <div className="rd-hero-metric">
              <span>Finalizados</span>
              <strong>{totals.finalizados}</strong>
            </div>
            <div className="rd-hero-metric">
              <span>Reprogramados</span>
              <strong>{totals.reprogramados}</strong>
            </div>
            <div className="rd-hero-metric">
              <span>Criados fora do esperado</span>
              <strong>{totals.prefixoE}</strong>
            </div>
            <div className="rd-hero-metric">
              <span>Em aberto</span>
              <strong>{totals.abertos}</strong>
            </div>
            <div className="rd-hero-metric">
              <span>Quantidade (Plan/Exec)</span>
              <strong>
                {quantidadeTotals.planejado} / {quantidadeTotals.executado}
              </strong>
            </div>
            <div className="rd-hero-metric">
              <span>Tempo (Plan/Exec)</span>
              <strong>
                {fmtSeconds(tempoTotals.planejado)} / {fmtSeconds(tempoTotals.executado)}
              </strong>
            </div>
          </div>
        </div>
      </header>

      <section className="rd-kpi-grid">
        {kpis.map((k) => (
          <div key={`${label}-${k.label}`} className="rd-kpi">
            <div>
              <div className="rd-kpi-label">{k.label}</div>
              <div className="rd-kpi-value">{k.value}</div>
            </div>
          </div>
        ))}
      </section>

      <section className="rd-grid">
        <div className="rd-card rd-chart">
          <div className="rd-card-head">
            <strong>Indicadores do dia</strong>
            <span>Meta, instabilidade e comparativo</span>
          </div>
          <div className="rd-summary-grid">
            <div className="rd-summary-block">
              <div className="rd-summary-title">Meta do dia (alvo: 90%)</div>
              <div className={`rd-progress tone-${metaScale.tone}`}>
                <div className="rd-progress-bar" style={{ width: `${metaPct}%` }} />
              </div>
              <div className="rd-progress-meta">
                <span>
                  {totals.finalizados} / {totals.programados}
                </span>
                <strong className={`rd-progress-value tone-${metaScale.tone}`}>
                  {metaPctDisplay.toFixed(1)}%
                </strong>
              </div>
            </div>

            <div className="rd-summary-block">
              <div className="rd-summary-title">Indicador de instabilidade</div>
              <div className={`rd-progress tone-${estabilidadeScale.tone}`}>
                <div className="rd-progress-bar" style={{ width: `${estabilidadePct}%` }} />
              </div>
              <div className="rd-progress-meta">
                <span>
                  {totals.reprogramados} / {totals.programados}
                </span>
                <strong className={`rd-progress-value tone-${estabilidadeScale.tone}`}>
                  {estabilidadePctDisplay.toFixed(1)}%
                </strong>
              </div>
            </div>

            <div className="rd-summary-block">
              <div className="rd-summary-title">Comparativo com dia anterior</div>
              <div className="rd-compare-list">
                {compareItems.map((item) => (
                  <div key={item.label} className="rd-compare-row">
                    <div>
                      <strong>{item.label}</strong>
                      <span>
                        {item.value} (prev: {item.prev})
                      </span>
                    </div>
                    <span className="rd-compare-delta">{item.delta}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="rd-card rd-chart">
          <div className="rd-card-head">
            <strong>Tendencia de conclusao da semana</strong>
            <span>
              De {fmtDayLabel(trendSeries[0]?.data)} ate {fmtDayLabel(trendSeries[6]?.data)}
            </span>
          </div>
          <div className="rd-trend">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <linearGradient id={trendGradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                  {trendPlotPoints.map((p, idx) => (
                    <stop key={idx} offset={`${p.x}%`} stopColor={p.color} />
                  ))}
                </linearGradient>
              </defs>
              <line
                x1="0"
                y1={trendTargetY}
                x2="100"
                y2={trendTargetY}
                className="rd-trend-threshold"
                style={{ stroke: trendThresholdColor }}
              />
              {trendPlotPoints.map((p, idx) => (
                <line key={`g-${idx}`} x1={p.x} y1={TREND_PLOT.bottom} x2={p.x} y2={p.y} className="rd-trend-guide" />
              ))}
              {trendSmoothPath && (
                <path
                  d={trendSmoothPath}
                  className="rd-trend-polyline"
                  style={{ stroke: `url(#${trendGradientId})` }}
                />
              )}
              {trendPlotPoints.map((p, idx) => (
                <circle key={idx} cx={p.x} cy={p.y} r="2" className="rd-trend-dot" style={{ fill: p.color }} />
              ))}
              {trendPlotPoints.map((p, idx) => {
                const isNearTop = p.y <= TREND_PLOT.top + 4;
                const labelY = isNearTop
                  ? Math.min(TREND_PLOT.bottom - 2, p.y + 7)
                  : Math.max(TREND_PLOT.top + 2, p.y - 3);
                return (
                  <text
                    key={`tv-${idx}`}
                    className="rd-trend-value"
                    x={p.x}
                    y={labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    {p.value}%
                  </text>
                );
              })}
            </svg>
            <div className="rd-trend-dates" aria-hidden="true">
              {trendPoints.map((p, idx) => (
                <span key={`td-${idx}`} className="rd-trend-date-chip">
                  {fmtDayShort(p.label)}
                </span>
              ))}
            </div>
          </div>
          <div className="rd-legend">
            <span>
              <i className="dot" style={{ background: trendThresholdColor }} /> Tendencia de conclusao da semana
            </span>
            <span>Linha de corte (meta): {trendTargetPct}%</span>
            <span>Inicio: {trendResume.start}%</span>
            <span>Atual: {trendResume.end}%</span>
          </div>
          {!trendPlotPoints.length && <div className="rd-empty">Sem dados na semana.</div>}
        </div>

        <div className="rd-card rd-chart">
          <div className="rd-card-head">
            <strong>Tipos de programacao</strong>
            <span>Programacoes feitas no dia anterior, no dia e na semanal</span>
          </div>
          <div className="rd-chart-figure">
            <div className="rd-donut">
              <svg className="rd-donut-svg" viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`} aria-hidden="true">
                <circle
                  className="rd-donut-track"
                  cx={DONUT_CENTER}
                  cy={DONUT_CENTER}
                  r={DONUT_RADIUS}
                />
                {prefixArcSegments.map((item) => (
                  <circle
                    key={item.key}
                    className="rd-donut-arc"
                    cx={DONUT_CENTER}
                    cy={DONUT_CENTER}
                    r={DONUT_RADIUS}
                    stroke={item.color}
                    strokeDasharray={`${item.arc} ${DONUT_CIRC}`}
                    strokeDashoffset={item.offset}
                    transform={`rotate(-90 ${DONUT_CENTER} ${DONUT_CENTER})`}
                  />
                ))}
              </svg>
              <div className="rd-donut-center">
                <strong>{prefixTotal}</strong>
              </div>
              {prefixLabelPositions.map((item) => (
                <div
                  key={item.key}
                  className="rd-donut-label"
                  title={item.label}
                  style={{
                    left: `${item.labelX}px`,
                    top: `${item.labelY}px`,
                  }}
                >
                  {item.pct}%
                </div>
              ))}
            </div>
          </div>
          <div className="rd-legend">
            {prefixActiveSegments.map((item) => (
              <span key={item.key}>
                <i className={`dot ${item.dotClass}`} /> {item.label}
              </span>
            ))}
            {!prefixActiveSegments.length && <span>Sem programacoes no periodo.</span>}
          </div>
        </div>

        <div className="rd-card rd-chart">
          <div className="rd-card-head">
            <strong>Taxa de conclusao</strong>
            <span>Finalizados sobre programados</span>
          </div>
          <div className="rd-chart-figure">
            <div
              className="rd-ring"
              style={{
                "--rd-ring-color": completionScale.color,
              }}
            >
              <svg className="rd-ring-svg" viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`} aria-hidden="true">
                <circle className="rd-ring-track" cx={DONUT_CENTER} cy={DONUT_CENTER} r={DONUT_RADIUS} />
                <circle
                  className="rd-ring-progress"
                  cx={DONUT_CENTER}
                  cy={DONUT_CENTER}
                  r={DONUT_RADIUS}
                  stroke={completionScale.color}
                  strokeDasharray={`${completionArc} ${DONUT_CIRC}`}
                  transform={`rotate(-90 ${DONUT_CENTER} ${DONUT_CENTER})`}
                />
              </svg>
              <div className="rd-ring-center">
                <strong>{completion}%</strong>
                <em className={`rd-ring-grade ${completionScale.tone}`}>{completionScale.label}</em>
              </div>
            </div>
          </div>
          <div className="rd-legend">
            <span>
              <i className="dot dot-a" /> Finalizados: {totals.finalizados}
            </span>
          </div>
        </div>

        <div className="rd-card rd-chart">
          <div className="rd-card-head">
            <strong>Conclusao de tempo</strong>
            <span>Executado vs planejado</span>
          </div>
          <div className="rd-chart-figure">
            <div
              className="rd-ring"
              style={{
                "--rd-ring-color": tempoScale.color,
              }}
            >
              <svg className="rd-ring-svg" viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`} aria-hidden="true">
                <circle className="rd-ring-track" cx={DONUT_CENTER} cy={DONUT_CENTER} r={DONUT_RADIUS} />
                <circle
                  className="rd-ring-progress"
                  cx={DONUT_CENTER}
                  cy={DONUT_CENTER}
                  r={DONUT_RADIUS}
                  stroke={tempoScale.color}
                  strokeDasharray={`${tempoArc} ${DONUT_CIRC}`}
                  transform={`rotate(-90 ${DONUT_CENTER} ${DONUT_CENTER})`}
                />
              </svg>
              <div className="rd-ring-center">
                <strong>{tempoPct}%</strong>
                <em className={`rd-ring-grade ${tempoScale.tone}`}>{tempoScale.label}</em>
              </div>
            </div>
          </div>
          <div className="rd-legend">
            <span>
              <i className="dot dot-a" /> Planejado: {fmtSeconds(tempoTotals.planejado)}
            </span>
            <span>
              <i className="dot dot-b" /> Executado: {fmtSeconds(tempoTotals.executado)}
            </span>
          </div>
        </div>

        <div className="rd-card rd-chart">
          <div className="rd-card-head">
            <strong>Conclusao de quantidade</strong>
            <span>Executado vs planejado</span>
          </div>
          <div className="rd-chart-figure">
            <div
              className="rd-ring"
              style={{
                "--rd-ring-color": quantidadeScale.color,
              }}
            >
              <svg className="rd-ring-svg" viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`} aria-hidden="true">
                <circle className="rd-ring-track" cx={DONUT_CENTER} cy={DONUT_CENTER} r={DONUT_RADIUS} />
                <circle
                  className="rd-ring-progress"
                  cx={DONUT_CENTER}
                  cy={DONUT_CENTER}
                  r={DONUT_RADIUS}
                  stroke={quantidadeScale.color}
                  strokeDasharray={`${quantidadeArc} ${DONUT_CIRC}`}
                  transform={`rotate(-90 ${DONUT_CENTER} ${DONUT_CENTER})`}
                />
              </svg>
              <div className="rd-ring-center">
                <strong>{quantidadePct}%</strong>
                <em className={`rd-ring-grade ${quantidadeScale.tone}`}>{quantidadeScale.label}</em>
              </div>
            </div>
          </div>
          <div className="rd-legend">
            <span>
              <i className="dot dot-a" /> Planejado: {quantidadeTotals.planejado}
            </span>
            <span>
              <i className="dot dot-b" /> Executado: {quantidadeTotals.executado}
            </span>
          </div>
        </div>

        <div className="rd-card rd-chart rd-deviation-card">
          <div className="rd-card-head">
            <strong>Desvio de atividades</strong>
            <span>Piores (perdas) e melhores (ganhos) no periodo</span>
          </div>
          <div className="rd-deviation-grid">
            <div className="rd-deviation-block">
              <div className="rd-deviation-title">Top 5 piores</div>
              <div className="rd-deviation-list">
                {desviosTop.pior.map((d) => (
                  <div key={d.atividade} className="rd-deviation-row">
                    <div className="rd-deviation-name">{d.atividade}</div>
                    <div className="rd-deviation-metrics">
                      {d.tempoDiff < 0 ? (
                        <span>
                          Tempo perdido: {fmtSeconds(Math.abs(d.tempoDiff))} ({d.tempoPctAbs}%)
                        </span>
                      ) : (
                        <span>Tempo no alvo</span>
                      )}
                      {d.qtdDiff < 0 ? (
                        <span>Quantidade perdida: {Math.abs(d.qtdDiff)} ({d.qtdPctAbs}%)</span>
                      ) : (
                        <span>Quantidade no alvo</span>
                      )}
                    </div>
                  </div>
                ))}
                {!desviosTop.pior.length && <div className="rd-empty">Sem perdas no periodo.</div>}
              </div>
            </div>

            <div className="rd-deviation-block">
              <div className="rd-deviation-title">Top 5 melhores</div>
              <div className="rd-deviation-list">
                {desviosTop.melhor.map((d) => (
                  <div key={d.atividade} className="rd-deviation-row">
                    <div className="rd-deviation-name">{d.atividade}</div>
                    <div className="rd-deviation-metrics">
                      {d.tempoDiff > 0 ? (
                        <span>
                          Tempo ganho: {fmtSeconds(Math.abs(d.tempoDiff))} ({d.tempoPctAbs}%)
                        </span>
                      ) : (
                        <span>Tempo no alvo</span>
                      )}
                      {d.qtdDiff > 0 ? (
                        <span>Quantidade ganha: {Math.abs(d.qtdDiff)} ({d.qtdPctAbs}%)</span>
                      ) : (
                        <span>Quantidade no alvo</span>
                      )}
                    </div>
                  </div>
                ))}
                {!desviosTop.melhor.length && <div className="rd-empty">Sem ganhos no periodo.</div>}
              </div>
            </div>
          </div>
        </div>

        <div
          className="rd-card rd-activity-card"
          data-email-capture="cards"
          data-email-name={`Atividades programadas x executadas - ${isGeral ? "Geral" : label}`}
        >
          <div className="rd-card-head">
            <strong>Atividades programadas x executadas</strong>
            <span>Top 12 no periodo</span>
          </div>
          <div className="rd-activity-list">
            {atividadesOrdenadas.map((r) => {
              const tempoPlan = Number(r.tempo_planejado_s || 0);
              const tempoExec = Number(r.tempo_executado_s || 0);
              const qtdPlan = Number(r.quantidade_planejada || 0);
              const qtdExec = Number(r.quantidade_executada || 0);
              const tempoExecPctRaw = tempoPlan > 0 ? (tempoExec / tempoPlan) * 100 : 0;
              const tempoExecPct = Math.min(100, Math.round(tempoExecPctRaw));
              const tempoExecPctLabel = Math.max(0, Math.round(tempoExecPctRaw));
              const qtdExecPctRaw = qtdPlan > 0 ? (qtdExec / qtdPlan) * 100 : 0;
              const qtdExecPct = Math.min(100, Math.round(qtdExecPctRaw));
              const qtdExecPctLabel = Math.max(0, Math.round(qtdExecPctRaw));
              const tempoScaleRow = getPerformanceScale(tempoExecPctRaw);
              const qtdScaleRow = getPerformanceScale(qtdExecPctRaw);

              return (
                <div key={r.atividade} className="rd-activity-row">
                  <div className="rd-activity-name">{r.atividade}</div>
                  <div className="rd-activity-col">
                    <div className="rd-activity-col-title">Tempo</div>
                    <div className="rd-activity-bars">
                      <div
                        className={`rd-activity-bar done tone-${tempoScaleRow.tone}`}
                        style={{ width: `${tempoExecPct}%` }}
                        title={`Executado: ${fmtSeconds(tempoExec)}`}
                      >
                        <span className="rd-activity-bar-label">{tempoExecPctLabel}%</span>
                      </div>
                    </div>
                    <div className="rd-activity-values">
                      {fmtSeconds(tempoPlan)} / {fmtSeconds(tempoExec)} (
                      {tempoExec >= tempoPlan ? "Tempo ganho" : "Tempo perdido"}{" "}
                      {fmtSeconds(Math.abs(tempoExec - tempoPlan))})
                    </div>
                  </div>
                  <div className="rd-activity-col">
                    <div className="rd-activity-col-title">Quantidade</div>
                    <div className="rd-activity-bars">
                      <div
                        className={`rd-activity-bar done tone-${qtdScaleRow.tone}`}
                        style={{ width: `${qtdExecPct}%` }}
                        title={`Qtd executada: ${qtdExec}`}
                      >
                        <span className="rd-activity-bar-label">{qtdExecPctLabel}%</span>
                      </div>
                    </div>
                    <div className="rd-activity-values">
                      {qtdPlan} / {qtdExec} (
                      {qtdExec >= qtdPlan ? "Quantidade ganha" : "Quantidade perdida"}{" "}
                      {Math.abs(qtdExec - qtdPlan)})
                    </div>
                  </div>
                  <div className="rd-activity-counts">
                    <div>
                      {Number(r.registros_programados || 0)} /{" "}
                      {Number(r.registros_executados || 0)}
                    </div>
                  </div>
                </div>
              );
            })}
            {!atividadesOrdenadas.length && (
              <div className="rd-empty">Sem atividades no periodo.</div>
            )}
          </div>
        </div>

        <div className="rd-pareto-grid">
          <ParetoChart
            title="Pareto de causadores (Finalizados)"
            subtitle="Finalizados"
            rows={paretoFinalizadoRows}
          />
          <ParetoChart
            title="Pareto de causadores (Reprogramados)"
            subtitle="Reprogramados"
            rows={paretoReprogramadoRows}
          />
        </div>
      </section>
    </div>
  );
}

export default function RelatorioDiarioPage() {
  const today = useMemo(() => todayYMD(), []);
  const yesterday = useMemo(() => addDaysYMD(today, -1), [today]);
  const [inputs, setInputs] = useState({ day: yesterday });
  const [query, setQuery] = useState({ day: yesterday });
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const abortRef = useRef(null);
  const requestIdRef = useRef(0);
  const loadingRequestIdRef = useRef(0);

  const loadData = useCallback(async (payload, { silent } = {}) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (!silent) {
      loadingRequestIdRef.current = requestId;
      setLoading(true);
    }
    setError("");

    try {
      const base = await getRelatorioDiario({
        day: payload.day,
        signal: controller.signal,
      });

      const list = Array.isArray(base?.gerencias) ? base.gerencias : [];
      const names = list.map((g) => g.gerencia).filter(Boolean);
      const details = await Promise.all(
        names.map((g) =>
          getRelatorioDiario({ day: payload.day, gerencia: g, signal: controller.signal })
        )
      );

      const nextSections = [
        { key: "geral", label: "Geral", data: base },
        ...names.map((g, idx) => ({ key: g, label: g, data: details[idx] })),
      ];

      if (requestId !== requestIdRef.current) return;
      setSections(nextSections);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;
      if (requestId !== requestIdRef.current) return;
      setError(err?.message || "Falha ao carregar relatorio.");
    } finally {
      if (!silent && requestId === loadingRequestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(query);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [query, loadData]);

  useEffect(() => {
    const id = setInterval(() => loadData(query, { silent: true }), 30000);
    return () => clearInterval(id);
  }, [query, loadData]);

  return (
    <div className="rd-page">
      <section
        className="rd-filters"
        data-email-capture="cards"
        data-email-name="Topo - filtros"
      >
        <Link className="rd-btn rd-btn-secondary" to="/">
          Menu inicial
        </Link>
        <div className="rd-field">
          <input
            type="date"
            value={inputs.day}
            onChange={(e) => {
              const value = e.target.value;
              setInputs({ day: value });
              if (value) {
                setLoading(true);
                setError("");
                setQuery((q) => ({ ...q, day: value }));
              }
            }}
          />
        </div>
        {loading && sections.length > 0 && (
          <div className="rd-loading" role="status" aria-label="Carregando relatorio">
            <span className="rd-spinner" aria-hidden="true" />
          </div>
        )}
        {error && <div className="rd-error">{error}</div>}
      </section>

      {sections.map((section) => (
        <RelatorioDiarioSection
          key={section.key}
          label={section.label}
          day={query.day}
          data={section.data}
        />
      ))}

      {!sections.length &&
        (loading ? (
          <div className="rd-loading rd-loading-center" role="status" aria-label="Carregando relatorio">
            <span className="rd-spinner" aria-hidden="true" />
          </div>
        ) : (
          <div className="rd-empty">Sem dados no periodo.</div>
        ))}
    </div>
  );
}


